import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ShieldCheck, Undo2, Search, CheckCircle2, XCircle, RotateCw, FileText, X, AlertTriangle, TrendingUp, MapPin, Building2, User, Sparkles, Users as UsersIcon, Zap, FileSearch } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, Input } from "../../components/common/Primitives";
import KycSlaCountdown from "../../components/admin/KycSlaCountdown";
import PassportRedactor from "../../components/admin/PassportRedactor";
import ConfirmReasonDialog from "../../components/common/ConfirmReasonDialog";
import api, { formatApiError } from "../../lib/api";

const BULK_CONFIRM_THRESHOLD = 20;

/**
 * KYC reviewer note templates — dropdown options on the drawer decision
 * panel. Cover the 90% of reviewer feedback so decisions ship in seconds.
 */
const KYC_NOTE_TEMPLATES = [
  { label: "Verified — clean KYC", status: "Approved", note: "All documents verified — KYC approved." },
  { label: "Missing PAN", status: "More Information Required", note: "PAN document is missing. Please upload a clear scan of the applicant's PAN card (front side, all details visible)." },
  { label: "Bank statement mismatch", status: "More Information Required", note: "The bank statement provided does not match the entity name on the KYC. Please upload the last 3 months' statement for the same registered entity." },
  { label: "Address proof unclear", status: "More Information Required", note: "The address proof is illegible or expired. Please upload a recent (within 3 months) utility bill / registered address certificate." },
  { label: "Signature / photograph mismatch", status: "More Information Required", note: "Signature on documents does not match the authorised signatory on file. Please re-submit with the correct signatory or attach a board resolution." },
  { label: "Entity mismatch on documents", status: "More Information Required", note: "The legal entity name across submitted documents is inconsistent. Please ensure all uploads reference the same registered entity." },
  { label: "Duplicate application", status: "Rejected", note: "A KYC application for this entity already exists on VenturaZ. Please continue with the original profile or contact support to merge." },
  { label: "Sanctioned / disqualified", status: "Rejected", note: "This applicant does not meet the VenturaZ eligibility criteria on the current sanctions / disqualification checks." },
];

/**
 * ApprovalsCockpit — Super-admin one-stop approval workspace.
 *
 * Tabs: Startups · Investors · History.
 * Features: search, multi-select with bulk approve/reject, per-row
 * approve/reject, KYC drawer (docs + status), rollback of any recent action.
 * Deep-link: `/admin/approvals?tab=investors&entity=<id>` auto-opens the
 * KYC drawer for that entity — used by KYC notification links.
 */
export default function AdminApprovalsCockpit() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "investors" || params.get("tab") === "history"
    ? params.get("tab") : "startups";
  const [tab, setTab] = useState(initialTab);
  const [startups, setStartups] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [kycDrawer, setKycDrawer] = useState(null); // {kind, id}
  const [kycPayload, setKycPayload] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action, count } | null
  const [preview, setPreview] = useState(null); // { row, kind } | null
  const [reviewerTemplates, setReviewerTemplates] = useState(KYC_NOTE_TEMPLATES);
  const [bulkKycConfirmOpen, setBulkKycConfirmOpen] = useState(false);
  const previewTimerRef = useRef(null);

  useEffect(() => {
    // Load reviewer templates from admin settings so super-admin edits
    // propagate without a client rebuild. Falls back to the built-in list.
    api.get("/admin/settings")
      .then((r) => {
        const t = r.data?.kyc?.reviewer_templates;
        if (Array.isArray(t) && t.length > 0) setReviewerTemplates(t);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const [s, i, h] = await Promise.all([
        api.get("/admin/startups"),
        api.get("/admin/investors"),
        api.get("/admin/approvals/history", { params: { limit: 100 } }),
      ]);
      setStartups(s.data || []);
      setInvestors(i.data || []);
      setHistory(h.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const rows = tab === "startups" ? startups : investors;
  const idKey = tab === "startups" ? "startup_id" : "investor_id";
  const nameKey = tab === "startups" ? "startup_name" : "investor_name";

  const filtered = useMemo(() => {    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && (r.status || "").toLowerCase() !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        r[nameKey], r.firm_name, r.founder_email, r.owner_email, r.owner_user_id,
        r.sector, r.investor_type, r.city,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, statusFilter, nameKey]);

  // Count of Under-Review investors with at least one doc — powers the
  // "Approve all queued KYC" button on the Investors tab.
  const underReviewCount = useMemo(() => {
    if (tab !== "investors") return 0;
    return investors.filter((r) => (r.kyc_status === "Under Review")
      && (r.kyc_doc_count || 0) > 0).length;
  }, [tab, investors]);

  const approveAllQueuedKyc = () => {
    if (underReviewCount === 0) return;
    setBulkKycConfirmOpen(true);
  };
  const runApproveAllQueuedKyc = async () => {
    setBulkKycConfirmOpen(false);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/investors/bulk-kyc-approve", {
        only_with_docs: true,
      });
      if ((data.approved || 0) === 0) {
        toast.info(`Nothing to approve · ${data.skipped || 0} skipped.`);
      } else {
        // Custom undo toast — reverses via /admin/investors/bulk-kyc-rollback.
        toast.success(
          `Approved ${data.approved} KYC${data.approved === 1 ? "" : "s"}${data.skipped ? ` · Skipped ${data.skipped}` : ""} · Tap Undo to reverse`,
          {
            duration: 10000,
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  setBusy(true);
                  const res = await api.post("/admin/investors/bulk-kyc-rollback", {
                    investor_ids: data.approved_ids || [],
                    reason: "Reviewer undid bulk KYC approval.",
                  });
                  toast.success(`Rolled back ${res.data?.rolled_back || 0} approval${(res.data?.rolled_back || 0) === 1 ? "" : "s"}.`);
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setBusy(false); }
              },
            },
          }
        );
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r[idKey])));
  };

  const bulk = async (action) => {
    if (selected.size === 0) return toast.error("Select at least one row.");
    // Threshold guard — reject any large batch OR approve batches >20 need
    // a confirm step (a stray click on a 97-row select-all could otherwise
    // undo a week's onboarding pipeline).
    if (selected.size > BULK_CONFIRM_THRESHOLD) {
      setConfirm({ action, count: selected.size });
      return;
    }
    return runBulk(action);
  };

  const emitUndoToast = (action, changedList) => {
    if (!changedList || changedList.length === 0) return;
    const verb = action === "approve" ? "Approved" : "Rejected";
    // Sonner toast with an inline Undo action — reverses every history_id
    // written by this bulk call. Auto-dismisses after 10 s.
    toast.success(`${verb} ${changedList.length} · Tap Undo to reverse`, {
      duration: 10000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            setBusy(true);
            const results = await Promise.allSettled(
              changedList.map((h) => api.post(`/admin/approvals/rollback/${h.history_id}`))
            );
            const succeeded = results.filter((r) => r.status === "fulfilled").length;
            const failed = results.length - succeeded;
            if (succeeded > 0) toast.success(`Undid ${succeeded} ${action}${failed ? ` · ${failed} skipped` : ""}`);
            if (succeeded === 0 && failed > 0) toast.error("Could not undo — check History tab");
            load();
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setBusy(false); }
        },
      },
    });
  };

  const runBulk = async (action) => {
    setBusy(true);
    setConfirm(null);
    try {
      const res = await api.post("/admin/approvals/bulk", {
        entity_type: tab === "startups" ? "startup" : "investor",
        entity_ids: [...selected],
        action,
        note: note || "",
      });
      const changed = res.data?.history || [];
      const skipped = selected.size - changed.length;
      if (changed.length === 0) {
        toast.info(`Nothing to ${action} · ${skipped} skipped (already set).`);
      } else {
        emitUndoToast(action, changed);
      }
      setNote("");
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const singleAction = async (id, action) => {
    setBusy(true);
    try {
      const res = await api.post("/admin/approvals/bulk", {
        entity_type: tab === "startups" ? "startup" : "investor",
        entity_ids: [id],
        action,
        note: "",
      });
      const changed = res.data?.history || [];
      if (changed.length === 0) {
        toast.info("Already " + (action === "approve" ? "Approved" : "Rejected") + ".");
      } else {
        emitUndoToast(action, changed);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const rollback = async (historyId) => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/approvals/rollback/${historyId}`);
      toast.success(`Rolled back → ${res.data?.restored_to}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openKyc = useCallback(async (row) => {
    setKycDrawer({ kind: tab, id: row[idKey], name: row[nameKey] || row.firm_name || row.founder_email || row.owner_email || row[idKey] });
    setKycPayload(null);
    try {
      if (tab === "startups") {
        const r = await api.get(`/admin/startups/${row.startup_id}/kyc`);
        setKycPayload(r.data);
      } else {
        const r = await api.get(`/admin/investors/${row.investor_id}/kyc`);
        setKycPayload(r.data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [tab, idKey, nameKey]);

  // Deep-link handler — one-shot resolve of ?entity=<id> to open the KYC
  // drawer once rows arrive. Kicked from KYC email/notification links.
  const [deepLinkResolved, setDeepLinkResolved] = useState(false);
  useEffect(() => {
    if (deepLinkResolved) return;
    const entityId = params.get("entity");
    if (!entityId) { setDeepLinkResolved(true); return; }
    const source = tab === "startups" ? startups : investors;
    if (!source.length) return;
    const row = source.find((r) => r[idKey] === entityId);
    if (row) {
      openKyc(row);
      setDeepLinkResolved(true);
    }
  }, [params, tab, startups, investors, idKey, openKyc, deepLinkResolved]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    const next = new URLSearchParams(params);
    if (nextTab === "startups") next.delete("tab"); else next.set("tab", nextTab);
    setParams(next, { replace: true });
  };

  // ----- Row preview (hover) --------------------------------------------------
  const schedulePreview = (row) => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      setPreview({ row, kind: tab });
    }, 250);
  };
  const cancelPreview = () => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    // Small delay so users can move the mouse into the preview to click links.
    previewTimerRef.current = setTimeout(() => setPreview(null), 180);
  };
  const holdPreview = () => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
  };
  useEffect(() => () => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
  }, []);

  return (
    <DashboardShell title="Approvals Cockpit" subtitle="ADMIN · UNIFIED APPROVALS">
      <Toaster richColors position="top-right" />
      <p className="text-ink-soft text-sm mb-4 max-w-2xl">
        One workspace to onboard, unblock and roll back startup + investor approvals.
        Bulk-approve verified applicants, or drill into KYC before flipping status.
      </p>

      <div className="flex items-center gap-1 border-b border-navy/10 mb-6" data-testid="approvals-tabs">
        {[
          { key: "startups", label: `Startups · ${startups.length}` },
          { key: "investors", label: `Investors · ${investors.length}` },
          { key: "history", label: `History · ${history.length}` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => changeTab(t.key)}
            className={`px-4 py-2 text-sm font-heading border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-emerald-600 text-emerald-800"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
            data-testid={`approvals-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== "history" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="flex items-center gap-2 flex-1 min-w-[220px] px-3 py-2 border border-navy/15 rounded-md bg-white">
              <Search size={14} className="text-ink-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${tab}…`}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint"
                data-testid="approvals-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-navy/15 rounded-md px-2 py-2 bg-white"
              data-testid="approvals-status-filter"
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="under review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <div className="flex items-center gap-2 flex-1 min-w-[220px] px-3 py-2 border border-navy/15 rounded-md bg-white">
              <FileText size={14} className="text-ink-muted" />
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reviewer note (optional, applies to bulk)"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint"
                data-testid="approvals-note"
              />
            </div>
            <Button
              variant="primary"
              onClick={() => bulk("approve")}
              disabled={busy || selected.size === 0}
              data-testid="approvals-bulk-approve"
            >
              <CheckCircle2 size={14} className="inline mr-1" /> Approve {selected.size || ""}
            </Button>
            <Button
              variant="danger"
              onClick={() => bulk("reject")}
              disabled={busy || selected.size === 0}
              data-testid="approvals-bulk-reject"
            >
              <XCircle size={14} className="inline mr-1" /> Reject {selected.size || ""}
            </Button>
            {tab === "investors" && underReviewCount > 0 && (
              <button
                onClick={approveAllQueuedKyc}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-emerald-400 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-heading disabled:opacity-50"
                title="Approve every Under-Review investor with at least one document uploaded."
                data-testid="approvals-bulk-kyc-all"
              >
                <Zap size={12} /> Approve all queued KYC · {underReviewCount}
              </button>
            )}
          </div>

          <Card testId="approvals-table-card">
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
                  <tr className="border-b border-navy/10">
                    <th className="text-left px-2 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && selected.size === filtered.length}
                        onChange={toggleAll}
                        className="w-4 h-4 accent-emerald-600"
                        data-testid="approvals-select-all"
                      />
                    </th>
                    <th className="text-left px-2 py-3">Name</th>
                    <th className="text-left px-2 py-3">
                      {tab === "startups" ? "Sector · Stage" : "Type · City"}
                    </th>
                    <th className="text-left px-2 py-3">Status</th>
                    <th className="text-left px-2 py-3">KYC</th>
                    <th className="text-left px-2 py-3">Docs</th>
                    <th className="text-right px-2 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const id = r[idKey];
                    const kycStatus = r.kyc_status || "Not Submitted";
                    return (
                      <tr
                        key={id}
                        className="border-b border-navy/5 hover:bg-emerald-50/20 relative"
                        data-testid={`approvals-row-${id}`}
                        onMouseEnter={() => schedulePreview(r)}
                        onMouseLeave={cancelPreview}
                      >
                        <td className="px-2 py-3">
                          <input
                            type="checkbox"
                            checked={selected.has(id)}
                            onChange={() => toggle(id)}
                            className="w-4 h-4 accent-emerald-600"
                            data-testid={`approvals-check-${id}`}
                          />
                        </td>
                        <td className="px-2 py-3 text-ink font-heading">
                          <div>{r[nameKey] || r.firm_name || r.founder_email || r.owner_email || id}</div>
                          <div className="text-[10px] text-ink-muted mt-0.5">
                            {tab === "startups"
                              ? r.founder_email
                              : (r.owner_email || r.owner_user_id)}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-ink-soft">
                          {tab === "startups"
                            ? `${r.sector || "—"} · ${r.business_stage || "—"}`
                            : `${r.investor_type || "—"} · ${r.city || "—"}`}
                        </td>
                        <td className="px-2 py-3">
                          <Pill tone={statusTone(r.status)}>{r.status || "Draft"}</Pill>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Pill tone={kycTone(kycStatus)}>{kycStatus}</Pill>
                            {tab === "investors" && r.kyc_target_at && (
                              <KycSlaCountdown
                                targetTime={r.kyc_target_at}
                                slaHours={r.kyc_sla_hours || 48}
                                status={kycStatus}
                                testId={`inv-kyc-sla-${id}`}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-ink-soft">
                          <button
                            onClick={() => openKyc(r)}
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 hover:underline"
                            data-testid={`approvals-kyc-${id}`}
                          >
                            <ShieldCheck size={12} /> {tab === "startups" ? "Review" : (r.kyc_doc_count || 0)} {tab === "investors" && "docs"}
                          </button>
                        </td>
                        <td className="px-2 py-3 text-right whitespace-nowrap space-x-1">
                          <button
                            onClick={() => singleAction(id, "approve")}
                            disabled={busy || r.status === "Approved"}
                            className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`approvals-approve-${id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => singleAction(id, "reject")}
                            disabled={busy || r.status === "Rejected"}
                            className="text-xs px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`approvals-reject-${id}`}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-ink-faint">
                      No {tab} match your filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {tab === "history" && (
        <Card testId="approvals-history-card">
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
                <tr className="border-b border-navy/10">
                  <th className="text-left px-2 py-3">When</th>
                  <th className="text-left px-2 py-3">Entity</th>
                  <th className="text-left px-2 py-3">Change</th>
                  <th className="text-left px-2 py-3">Actor</th>
                  <th className="text-left px-2 py-3">Note</th>
                  <th className="text-right px-2 py-3">Rollback</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.history_id} className="border-b border-navy/5" data-testid={`history-row-${h.history_id}`}>
                    <td className="px-2 py-3 font-mono text-[11px] text-ink-muted">
                      {(h.at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-2 py-3 text-ink font-heading">
                      <div>{h.entity_name}</div>
                      <div className="text-[10px] text-ink-muted uppercase font-mono">{h.entity_type}</div>
                    </td>
                    <td className="px-2 py-3 text-ink-soft">
                      <span className="text-ink-muted">{h.previous_status || "—"}</span>
                      <span className="mx-1 text-emerald-600">→</span>
                      <span className="text-ink">{h.new_status}</span>
                    </td>
                    <td className="px-2 py-3 text-ink-soft text-xs">{h.actor_email}</td>
                    <td className="px-2 py-3 text-xs italic text-ink-muted">{h.note || "—"}</td>
                    <td className="px-2 py-3 text-right">
                      {h.rolled_back ? (
                        <Pill tone="muted">Rolled back</Pill>
                      ) : (
                        <button
                          onClick={() => rollback(h.history_id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-50"
                          data-testid={`history-rollback-${h.history_id}`}
                        >
                          <Undo2 size={12} /> Rollback
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-ink-faint">
                    No approval events yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {kycDrawer && (
        <KycDrawer
          drawer={kycDrawer}
          payload={kycPayload}
          templates={reviewerTemplates}
          onClose={() => setKycDrawer(null)}
          onKycAction={async (status, note) => {
            try {
              const path = kycDrawer.kind === "investors"
                ? `/admin/investors/${kycDrawer.id}/kyc-status`
                : `/admin/startups/${kycDrawer.id}/kyc-status`;
              await api.post(path, { status, note: note || undefined });
              toast.success(`KYC marked ${status}.`);
              setKycDrawer(null);
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}

      {confirm && (
        <BulkConfirmModal
          action={confirm.action}
          count={confirm.count}
          entityLabel={tab === "startups" ? "startups" : "investors"}
          note={note}
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => runBulk(confirm.action)}
        />
      )}

      {preview && (
        <RowPreview
          row={preview.row}
          kind={preview.kind}
          onMouseEnter={holdPreview}
          onMouseLeave={cancelPreview}
          onOpenKyc={() => { openKyc(preview.row); setPreview(null); }}
          onApprove={() => { singleAction(preview.row[preview.kind === "startups" ? "startup_id" : "investor_id"], "approve"); setPreview(null); }}
          onReject={() => { singleAction(preview.row[preview.kind === "startups" ? "startup_id" : "investor_id"], "reject"); setPreview(null); }}
        />
      )}

      <ConfirmReasonDialog
        open={bulkKycConfirmOpen}
        title={`Approve KYC for all ${underReviewCount} Under-Review investors with docs on file?`}
        description="Investors with zero uploaded documents will be skipped automatically. Each approval sends the applicant an in-app + email confirmation. You can Undo the entire batch from the toast that appears next."
        confirmLabel={`Approve ${underReviewCount} KYC`}
        tone="emerald"
        reasonRequired={false}
        reasonLabel="Optional audit note"
        reasonPlaceholder=""
        onConfirm={runApproveAllQueuedKyc}
        onClose={() => setBulkKycConfirmOpen(false)}
        testId="approvals-bulk-kyc-confirm-dialog"
      />
    </DashboardShell>
  );
}

function RowPreview({ row, kind, onMouseEnter, onMouseLeave, onOpenKyc, onApprove, onReject }) {
  const isStartup = kind === "startups";
  const name = isStartup
    ? (row.startup_name || row.legal_entity_name || row.founder_email)
    : (row.investor_name || row.firm_name || row.owner_email || row.owner_user_id);
  const sub = isStartup
    ? [row.sector, row.business_stage, row.industry].filter(Boolean).join(" · ")
    : [row.investor_type, row.city, row.firm_name].filter(Boolean).join(" · ");
  const pitch = row.one_line_pitch || row.thesis || "";
  return (
    <div
      className="fixed top-24 right-6 z-[85] w-96 max-w-[calc(100vw-2rem)] bg-white border border-navy/10 rounded-lg shadow-2xl overflow-hidden"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid="approvals-row-preview"
    >
      <div className="px-4 py-3 border-b border-navy/10 bg-gradient-to-br from-emerald-50/60 via-white to-gold/[0.04]">
        <div className="flex items-center gap-2 overline">
          {isStartup ? <Building2 size={12} className="text-emerald-700" /> : <User size={12} className="text-emerald-700" />}
          {isStartup ? "Startup preview" : "Investor preview"}
        </div>
        <div className="font-heading text-ink text-lg mt-1 truncate">{name}</div>
        {sub && <div className="text-[11px] text-ink-muted font-mono mt-0.5">{sub}</div>}
      </div>
      <div className="p-4 space-y-3">
        {pitch && (
          <div>
            <div className="overline flex items-center gap-1"><Sparkles size={10} className="text-gold" /> Pitch</div>
            <div className="text-sm text-ink-soft mt-0.5 leading-relaxed">{pitch}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {isStartup ? (
            <>
              <PreviewStat icon={<TrendingUp size={11} />} label="Funding ask" value={formatINR(row.funding_ask)} sub={row.funding_type || ""} />
              <PreviewStat icon={<UsersIcon size={11} />} label="Team size" value={row.num_employees || "—"} sub={row.year_of_incorporation ? `Since ${row.year_of_incorporation}` : ""} />
              <PreviewStat icon={<MapPin size={11} />} label="Location" value={row.city || "—"} sub={row.state || row.country || ""} />
              <PreviewStat icon={<ShieldCheck size={11} />} label="Trust" value={row.company_verification_status ? row.company_verification_status.replace(/_/g, " ") : "—"} sub={row.founder_premium ? "Founder Premium" : ""} />
            </>
          ) : (
            <>
              <PreviewStat icon={<TrendingUp size={11} />} label="AUM" value={formatAUM(row.aum)} sub={row.investment_horizon || ""} />
              <PreviewStat icon={<Sparkles size={11} />} label="Ticket" value={formatINR(row.min_ticket_size || row.ticket_size)} sub={row.max_ticket_size ? `Up to ${formatINR(row.max_ticket_size)}` : ""} />
              <PreviewStat icon={<MapPin size={11} />} label="Location" value={row.city || "—"} sub={row.country || ""} />
              <PreviewStat icon={<ShieldCheck size={11} />} label="KYC" value={row.kyc_status || "Not Submitted"} sub={`${row.kyc_doc_count || 0} docs`} />
            </>
          )}
        </div>
        {(row.founder_email || row.owner_email) && (
          <div className="text-[10px] text-ink-muted font-mono truncate">
            {isStartup ? row.founder_email : row.owner_email}
          </div>
        )}
      </div>
      <div className="px-4 py-3 border-t border-navy/10 bg-navy/[0.02] flex items-center justify-between gap-2">
        <button
          onClick={onOpenKyc}
          className="text-xs text-emerald-800 hover:text-emerald-900 font-heading inline-flex items-center gap-1"
          data-testid="preview-open-kyc"
        >
          <ShieldCheck size={12} /> Open KYC
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={onApprove}
            disabled={row.status === "Approved"}
            className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="preview-approve"
          >
            Approve
          </button>
          <button
            onClick={onReject}
            disabled={row.status === "Rejected"}
            className="text-xs px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="preview-reject"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewStat({ icon, label, value, sub }) {
  return (
    <div className="border border-navy/10 rounded-md p-2 bg-white">
      <div className="overline flex items-center gap-1 text-ink-muted">{icon} {label}</div>
      <div className="font-heading text-ink text-sm mt-0.5 truncate" title={String(value)}>{value}</div>
      {sub && <div className="text-[10px] text-ink-muted font-mono truncate mt-0.5" title={sub}>{sub}</div>}
    </div>
  );
}

function formatINR(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n) || n === 0) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function formatAUM(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n) || n === 0) return typeof v === "string" ? v : "—";
  return formatINR(n);
}

function BulkConfirmModal({ action, count, entityLabel, note, busy, onCancel, onConfirm }) {
  const isReject = action === "reject";
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="approvals-confirm-modal">
      <div className="w-full max-w-md bg-white rounded-lg border border-navy/10 shadow-2xl overflow-hidden">
        <div className={`px-5 py-3 border-b border-navy/10 flex items-center gap-2 ${
          isReject ? "bg-rose-50" : "bg-amber-50"
        }`}>
          <AlertTriangle size={18} className={isReject ? "text-rose-700" : "text-amber-700"} />
          <div className={`font-heading text-sm uppercase tracking-widest ${
            isReject ? "text-rose-900" : "text-amber-900"
          }`}>
            Confirm bulk {action}
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-ink font-heading text-lg">
            {isReject ? "Reject" : "Approve"} <span className="text-rose-700">{count}</span> {entityLabel}?
          </div>
          <div className="text-sm text-ink-soft">
            You&apos;re about to {action} {count} {entityLabel} in one action.
            {isReject && " Their profiles will be marked Rejected and users will receive an email."}
            {" "}You can undo any single action from the History tab.
          </div>
          {note && (
            <div className="text-xs text-ink-muted bg-emerald-50/60 border border-emerald-100 rounded p-2 italic">
              Reviewer note that will be attached: &ldquo;{note}&rdquo;
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 rounded-md border border-navy/15 text-sm text-ink font-heading hover:bg-navy/[0.04]"
              data-testid="approvals-confirm-cancel"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              className={`px-4 py-2 rounded-md text-white text-sm font-heading disabled:opacity-50 ${
                isReject ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
              data-testid="approvals-confirm-proceed"
            >
              Yes, {action} {count}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KycDrawer({ drawer, payload, templates, onClose, onKycAction }) {
  const kind = drawer.kind === "investors" ? "Investor" : "Startup";
  const [note, setNote] = useState("");
  const [tmpl, setTmpl] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRedactor, setShowRedactor] = useState(false);
  const templateList = templates && templates.length > 0 ? templates : KYC_NOTE_TEMPLATES;
  const currentStatus = payload?.kyc_status || payload?.status || "Not Submitted";
  const canDecide = !!payload && currentStatus !== "Approved";

  const applyTemplate = (idx) => {
    setTmpl(idx);
    const t = templateList[parseInt(idx, 10)];
    if (t) setNote(t.note);
  };

  const submit = async (status) => {
    if (status !== "Approved" && !note.trim()) {
      toast.error(`A reviewer note is required to mark KYC as ${status}. Applicants can't fix what they don't see.`);
      return;
    }
    setBusy(true);
    try { await onKycAction(status, note.trim()); }
    finally { setBusy(false); }
  };

  const openPassportPreview = () => {
    setShowRedactor(true);
  };

  return (
    <div className="fixed inset-0 z-[70] flex" data-testid="kyc-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-2xl border-l border-navy/10 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-navy/10 px-5 py-3 flex items-center justify-between">
          <div className="font-heading text-ink flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" /> KYC · {kind}
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="kyc-drawer-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div className="font-heading text-ink text-base">{drawer.name}</div>
            {drawer.kind === "investors" && (
              <button
                onClick={openPassportPreview}
                className="text-[10px] font-heading uppercase tracking-widest text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1 border border-emerald-300 rounded-full px-2 py-1 hover:bg-emerald-50 flex-shrink-0"
                title="Preview the KYC Passport PDF the investor will see"
                data-testid="kyc-passport-preview"
              >
                <FileSearch size={11} /> Preview passport
              </button>
            )}
          </div>
          {!payload && <div className="text-sm text-ink-muted">Loading…</div>}
          {payload && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="overline">Status:</span>
                <Pill tone={kycTone(currentStatus)}>{currentStatus}</Pill>
                {payload.kyc_submitted_at && (
                  <span className="text-[10px] font-mono text-ink-muted">
                    Submitted {payload.kyc_submitted_at.slice(0, 10)}
                  </span>
                )}
              </div>
              {(payload.kyc_note || payload.note) && (
                <div className="text-xs italic text-ink-muted bg-emerald-50/40 border border-emerald-100 rounded p-2">
                  &ldquo;{payload.kyc_note || payload.note}&rdquo;
                </div>
              )}
              <div>
                <div className="overline mb-2">Uploaded documents · {(payload.documents || []).length}</div>
                {(payload.documents || []).length === 0 && (
                  <div className="text-sm text-ink-faint italic">No documents uploaded yet.</div>
                )}
                <div className="space-y-2">
                  {(payload.documents || []).map((d) => (
                    <div key={d.doc_id} className="border border-navy/10 rounded-md p-3 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink font-heading truncate">{d.filename || d.doc_id}</div>
                        <div className="text-[11px] text-ink-muted mt-0.5">
                          {d.category || "General"} · {formatSize(d.size)} · {(d.uploaded_at || "").slice(0, 10)}
                        </div>
                      </div>
                      <a
                        href={buildDocViewUrl(d.doc_id)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-emerald-700 hover:text-emerald-900 font-heading"
                        data-testid={`kyc-view-${d.doc_id}`}
                      >
                        View →
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              {/* KYC decision panel — visible for both investors + startups.
                  For investors this hits POST /admin/investors/{id}/kyc-status
                  which was previously wired backend-only. */}
              {canDecide && (
                <div className="border-t border-navy/10 pt-4 space-y-3" data-testid="kyc-decision-panel">
                  <div className="flex items-center justify-between gap-2">
                    <div className="overline">Reviewer decision</div>
                    <select
                      value={tmpl}
                      onChange={(e) => applyTemplate(e.target.value)}
                      className="text-xs bg-white border border-navy/15 rounded-md px-2 py-1 font-heading"
                      data-testid="kyc-note-template"
                      aria-label="Apply reviewer note template"
                    >
                      <option value="">Templates…</option>
                      {templateList.map((t, i) => (
                        <option key={t.label} value={i}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={`Note to the ${kind.toLowerCase()} — appears in-app and in the decision email. Required for Reject / More Info.`}
                    className="w-full border border-navy/15 rounded-md px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400"
                    data-testid="kyc-decision-note"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => submit("Approved")}
                      disabled={busy}
                      className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-heading disabled:opacity-50"
                      data-testid="kyc-approve"
                    >
                      Approve KYC
                    </button>
                    <button
                      onClick={() => submit("More Information Required")}
                      disabled={busy}
                      className="text-xs px-3 py-1.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 font-heading disabled:opacity-50"
                      data-testid="kyc-more-info"
                    >
                      More Info
                    </button>
                    <button
                      onClick={() => submit("Rejected")}
                      disabled={busy}
                      className="text-xs px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white font-heading disabled:opacity-50"
                      data-testid="kyc-reject"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
              {currentStatus === "Approved" && (
                <div className="border-t border-navy/10 pt-4 text-xs text-emerald-800 bg-emerald-50/40 border border-emerald-100 rounded p-2">
                  KYC is already approved. Use the History tab to roll back if needed.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showRedactor && drawer.kind === "investors" && (
        <PassportRedactor
          investorId={drawer.id}
          onClose={() => setShowRedactor(false)}
        />
      )}
    </div>
  );
}

function statusTone(status) {
  switch ((status || "").toLowerCase()) {
    case "approved": return "success";
    case "rejected": return "danger";
    case "under review":
    case "submitted": return "warn";
    default: return "muted";
  }
}

function kycTone(status) {
  switch ((status || "").toLowerCase()) {
    case "approved": return "success";
    case "rejected": return "danger";
    case "more information required":
    case "under review":
    case "submitted": return "warn";
    default: return "muted";
  }
}

function formatSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function buildDocViewUrl(docId) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/admin/documents/${docId}/view`;
}
